import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ruler, TreePine } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { WoodCalculator } from "@/components/WoodCalculator";
import { still } from "@/lib/media";

const PINO_CAT = "Madera de Pino Nacional";
const SPECS = [
    { label: "Largos", values: ["1,20 m", "2,44 m", "3,66 m"] },
    { label: "Anchos", values: ["10 cm", "15 cm", "20 cm", "25 cm", "30 cm"] },
    { label: "Espesor", values: ["25 mm"] },
];

export const PinoSection = () => {
    const [items, setItems] = useState([]);

    useEffect(() => {
        api.get("/products", { params: { categoria: PINO_CAT, limit: 4, sort: "precio_asc" } })
            .then((r) => setItems(r.data.items))
            .catch(() => {});
    }, []);

    return (
        <section id="pino" className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
                {/* Visual */}
                <div className="lg:col-span-5">
                    <Reveal>
                        <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-lift">
                            <img src={still("pino")} alt="Madera de pino nacional" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-verde-900/70 to-transparent" />
                            <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-madera text-white rounded-full px-3 py-1.5 text-xs font-semibold">
                                <TreePine size={14} /> Producción nacional 🇻🇪
                            </div>
                        </div>
                    </Reveal>
                </div>

                {/* Content */}
                <div className="lg:col-span-7">
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Nuestro producto estrella</span>
                        <h2 className="font-display font-bold text-3xl md:text-5xl text-ink mt-2 tracking-tight leading-tight">Madera de pino nacional</h2>
                        <p className="text-mut mt-4 max-w-xl leading-relaxed">
                            Tablas de pino cepillado de producción venezolana, listas para tu proyecto. Disponibles en múltiples medidas, al detal y al mayor.
                        </p>
                    </Reveal>

                    {/* Specs */}
                    <div className="mt-8 grid sm:grid-cols-3 gap-4">
                        {SPECS.map((s, i) => (
                            <Reveal key={s.label} delay={i * 0.06}>
                                <div className="bg-hueso border border-linea rounded-2xl p-5 h-full">
                                    <div className="flex items-center gap-2 text-verde mb-3">
                                        <Ruler size={16} />
                                        <span className="text-xs uppercase tracking-widest font-semibold">{s.label}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {s.values.map((v) => (
                                            <span key={v} className="text-sm font-medium text-ink bg-secondary rounded-full px-3 py-1">{v}</span>
                                        ))}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.1}>
                        <Link to={`/tienda?categoria=${encodeURIComponent(PINO_CAT)}`} data-testid="pino-shop-btn" className="mt-8 inline-flex items-center gap-2 bg-verde text-white rounded-full px-8 py-4 font-semibold hover:bg-verde-800 transition-colors">
                            Ver todas las medidas <ArrowRight size={18} />
                        </Link>
                    </Reveal>
                </div>
            </div>

            {/* Product preview */}
            {items.length > 0 && (
                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                    {items.map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
            )}

            {/* Calculator */}
            <div className="mt-12">
                <Reveal>
                    <WoodCalculator />
                </Reveal>
            </div>
        </section>
    );
};
